/**
 * Redemption Success Screen (Story 7.8)
 *
 * Full-screen success view displayed after a successful coupon redemption.
 * Replaces the old inline modal in RedemptionForm.
 *
 * Features:
 * - Animated green checkmark (AC1, AC8)
 * - Transaction summary card (AC2)
 * - Action buttons: New Redemption, View Receipt, Print (AC3)
 * - Printable receipt toggle (AC4)
 * - Responsive design for iPad/mobile (AC7)
 * - Full i18n support (AC6)
 */

import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, RotateCcw, Receipt, Printer } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { formatMoney, TimestampService } from '@zlinkx/shared-v2';
import type { CheckCouponCodeResponseType, CreateRedemptionResponseType } from '@zlinkx/shared-v2';
import RedemptionReceipt from './RedemptionReceipt';

type ValidatedCoupon = NonNullable<CheckCouponCodeResponseType['data']['coupon']>;

interface RedemptionSuccessProps {
	redemptionResult: CreateRedemptionResponseType;
	validatedCoupon: ValidatedCoupon;
	formContext: { invoiceId: string; note: string };
	onNewRedemption: () => void;
}

const RedemptionSuccess: React.FC<RedemptionSuccessProps> = ({
	redemptionResult,
	validatedCoupon,
	formContext,
	onNewRedemption,
}) => {
	const { t } = useTranslation();
	const [showReceipt, setShowReceipt] = useState(false);
	const titleRef = useRef<HTMLHeadingElement>(null);

	// AC8: Focus title on mount for accessibility
	useEffect(() => {
		titleRef.current?.focus();
	}, []);

	const getCouponTypeDisplay = (): string => {
		switch (validatedCoupon.couponType) {
			case 'percentage':
				return t('common.coupon.percentageOff', { value: validatedCoupon.discountValue ?? 0 });
			case 'fixed_amount':
				return t('common.coupon.amountOff', { value: (validatedCoupon.discountValue ?? 0) / 100 });
			case 'free_item':
			case 'bogo':
				return validatedCoupon.itemDescription ?? validatedCoupon.couponType;
			default:
				return validatedCoupon.couponType;
		}
	};

	const isFreeItemType = validatedCoupon.couponType === 'free_item' || validatedCoupon.couponType === 'bogo';

	const handlePrint = () => {
		window.print();
	};

	return (
		<div className="space-y-4 sm:space-y-6">
			{/* AC8: Screen reader announcement */}
			<div aria-live="polite" className="sr-only">
				{t('merchant.redemption.success.title')}
			</div>

			{/* Success Header Card */}
			<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 text-center">
				{/* AC1: Animated green checkmark */}
				<div className="flex justify-center mb-4">
					<CheckCircle
						className="w-20 h-20 text-green-500"
						role="img"
						aria-label={t('merchant.redemption.success.title')}
						style={{
							animation: 'scaleIn 300ms ease-out 200ms both',
						}}
					/>
				</div>

				{/* AC1: Title */}
				<h2
					ref={titleRef}
					tabIndex={-1}
					className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 outline-none"
				>
					{t('merchant.redemption.success.title')}
				</h2>
			</div>

			{/* AC2: Transaction Summary Card */}
			<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
				<div className="space-y-3">
					{/* Customer */}
					<div className="flex justify-between items-center">
						<span className="text-gray-600">{t('merchant.redemption.success.customerLabel')}</span>
						<span className="font-medium text-gray-900">
							{validatedCoupon.customerName ?? t('merchant.redemption.success.guestCustomer')}
						</span>
					</div>

					{/* Coupon Applied */}
					<div className="flex justify-between items-center">
						<span className="text-gray-600">{t('merchant.redemption.success.couponLabel')}</span>
						<span className="font-medium text-gray-900 text-right max-w-[60%] truncate">
							{validatedCoupon.title}
						</span>
					</div>

					{/* Coupon Type */}
					<div className="flex justify-between items-center">
						<span className="text-gray-600">{t('merchant.redemption.success.couponTypeLabel')}</span>
						<span className="text-gray-700">{getCouponTypeDisplay()}</span>
					</div>

					<div className="border-t border-gray-200 my-2" />

					{/* Original Amount */}
					<div className="flex justify-between items-center">
						<span className="text-gray-600">{t('merchant.redemption.success.originalAmount')}</span>
						<span className="text-gray-900">{formatMoney(redemptionResult.data.original_amount)}</span>
					</div>

					{/* Discount Amount */}
					<div className="flex justify-between items-center">
						<span className="text-gray-600">{t('merchant.redemption.success.discountAmount')}</span>
						<span className="font-medium text-green-600">
							{t('merchant.redemption.success.discountValue', {
								amount: formatMoney(redemptionResult.data.discount_amount),
								defaultValue: '-{{amount}}',
							})}
						</span>
					</div>

					{/* Free Items (if applicable) */}
					{isFreeItemType && validatedCoupon.itemDescription && (
						<div className="flex justify-between items-center">
							<span className="text-gray-600">{t('merchant.redemption.success.freeItemsLabel')}</span>
							<span className="text-green-600">{validatedCoupon.itemDescription}</span>
						</div>
					)}

					<div className="border-t border-gray-200 my-2" />

					{/* Final Amount - emphasized */}
					<div className="flex justify-between items-center bg-green-50 rounded-lg p-3 -mx-1">
						<span className="font-semibold text-gray-900">{t('merchant.redemption.success.finalAmount')}</span>
						<span className="font-bold text-lg text-gray-900">{formatMoney(redemptionResult.data.final_amount)}</span>
					</div>

					<div className="border-t border-gray-200 my-2" />

					{/* Invoice ID (conditional) */}
					{formContext.invoiceId && (
						<div className="flex justify-between items-center">
							<span className="text-gray-600">{t('merchant.redemption.success.invoiceLabel')}</span>
							<span className="text-gray-900">{formContext.invoiceId}</span>
						</div>
					)}

					{/* Note (conditional) */}
					{formContext.note && (
						<div className="flex justify-between items-center">
							<span className="text-gray-600">{t('merchant.redemption.success.noteLabel')}</span>
							<span className="text-gray-900 text-right max-w-[60%] truncate">{formContext.note}</span>
						</div>
					)}

					{/* Cashier */}
					<div className="flex justify-between items-center">
						<span className="text-gray-600">{t('merchant.redemption.success.cashierLabel')}</span>
						<span className="text-gray-900">{redemptionResult.data.cashier_name}</span>
					</div>

					{/* Timestamp */}
					<div className="flex justify-between items-center">
						<span className="text-gray-600">{t('merchant.redemption.success.timestampLabel')}</span>
						<span className="text-gray-900">
							{TimestampService.formatForDisplay(TimestampService.parseDateTimeInput(redemptionResult.timestamp))}
						</span>
					</div>
				</div>
			</div>

			{/* AC3: Action Buttons */}
			<div className="flex flex-col sm:flex-row gap-3">
				{/* Primary: New Redemption */}
				<button
					onClick={onNewRedemption}
					className="flex-1 bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 font-medium"
				>
					<RotateCcw className="w-5 h-5" />
					{t('merchant.redemption.success.newRedemption')}
				</button>

				{/* Secondary: View/Hide Receipt */}
				<button
					onClick={() => setShowReceipt(prev => !prev)}
					className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
					aria-label={showReceipt ? t('merchant.redemption.success.hideReceipt') : t('merchant.redemption.success.viewReceipt')}
				>
					<Receipt className="w-5 h-5" />
					{showReceipt
						? t('merchant.redemption.success.hideReceipt')
						: t('merchant.redemption.success.viewReceipt')
					}
				</button>

				{/* Secondary: Print */}
				<button
					onClick={handlePrint}
					className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
					aria-label={t('merchant.redemption.success.print')}
				>
					<Printer className="w-5 h-5" />
					{t('merchant.redemption.success.print')}
				</button>
			</div>

			{/* Receipt View (toggled) */}
			{showReceipt && (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
					<RedemptionReceipt
						redemptionResult={redemptionResult}
						validatedCoupon={validatedCoupon}
						formContext={formContext}
					/>
				</div>
			)}

			{/* CSS Keyframes for checkmark animation */}
			<style>{`
				@keyframes scaleIn {
					from { transform: scale(0); }
					to { transform: scale(1); }
				}
				@media print {
					body * {
						visibility: hidden !important;
					}
					#redemption-receipt,
					#redemption-receipt * {
						visibility: visible !important;
					}
					#redemption-receipt {
						position: absolute !important;
						left: 0 !important;
						top: 0 !important;
						width: 80mm !important;
						padding: 4mm !important;
						font-size: 11px !important;
					}
				}
			`}</style>
		</div>
	);
};

export default RedemptionSuccess;
